import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { findAirport } from "@/data/airports";
import { ExternalLink, Radio } from "lucide-react";

// Delete default icons config that requires public files
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const goldIcon = L.divIcon({
  className: "as-map-marker",
  html: `<div style="width:14px;height:14px;border-radius:50%;background:#A47E2A;border:2px solid #FFF;box-shadow:0 0 0 2px #A47E2A;"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const planeIcon = (rotation = 0) => L.divIcon({
  className: "as-map-plane",
  html: `<div style="transform:rotate(${rotation}deg);color:#A47E2A;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.15));">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
  </div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

function bearing(lat1, lon1, lat2, lon2) {
  const φ1 = lat1 * Math.PI / 180, φ2 = lat2 * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

/**
 * RouteMap: interactive OpenStreetMap of a flight route between two airports
 * @param {string} departureCode - ICAO/IATA
 * @param {string} arrivalCode
 * @param {string} tail - aircraft tail number for FR24 deep link
 */
export default function RouteMap({ departureCode, arrivalCode, tail }) {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const dep = findAirport(departureCode);
    const arr = findAirport(arrivalCode);
    if (!dep || !arr) return;

    const map = L.map(ref.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      subdomains: 'abcd',
    }).addTo(map);

    L.marker([dep.lat, dep.lon], { icon: goldIcon }).addTo(map).bindTooltip(`${dep.iata} · ${dep.city}`, { permanent: true, direction: "top", className: "as-tooltip" });
    L.marker([arr.lat, arr.lon], { icon: goldIcon }).addTo(map).bindTooltip(`${arr.iata} · ${arr.city}`, { permanent: true, direction: "top", className: "as-tooltip" });

    // Great-circle-ish curved polyline (quadratic Bézier interpolation)
    const points = [];
    const steps = 60;
    const midLat = (dep.lat + arr.lat) / 2;
    const midLon = (dep.lon + arr.lon) / 2;
    // Perpendicular offset to create arc
    const dLat = arr.lat - dep.lat;
    const dLon = arr.lon - dep.lon;
    const len = Math.hypot(dLat, dLon);
    const offset = len * 0.15;
    const nLat = -dLon / (len || 1);
    const nLon = dLat / (len || 1);
    const ctrlLat = midLat + nLat * offset;
    const ctrlLon = midLon + nLon * offset;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const lat = (1 - t) * (1 - t) * dep.lat + 2 * (1 - t) * t * ctrlLat + t * t * arr.lat;
      const lon = (1 - t) * (1 - t) * dep.lon + 2 * (1 - t) * t * ctrlLon + t * t * arr.lon;
      points.push([lat, lon]);
    }
    L.polyline(points, { color: "#A47E2A", weight: 2, opacity: 0.9, dashArray: "6 4" }).addTo(map);

    // Plane marker at ~62% along route with bearing
    const mid = points[Math.floor(points.length * 0.62)];
    const next = points[Math.floor(points.length * 0.62) + 1] || mid;
    const rot = bearing(mid[0], mid[1], next[0], next[1]);
    L.marker(mid, { icon: planeIcon(rot - 45) }).addTo(map);

    // Fit to route with padding
    const bounds = L.latLngBounds([[dep.lat, dep.lon], [arr.lat, arr.lon]]);
    map.fitBounds(bounds, { padding: [40, 40] });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [departureCode, arrivalCode]);

  const dep = findAirport(departureCode);
  const arr = findAirport(arrivalCode);
  const fr24Href = tail
    ? `https://www.flightradar24.com/data/aircraft/${tail}`
    : dep && arr
    ? `https://www.flightradar24.com/${dep.iata}/${arr.iata}`
    : "https://www.flightradar24.com/";

  return (
    <div className="as-card overflow-hidden" data-testid="route-map">
      <div className="flex items-center justify-between p-4 border-b border-[#E5DFD2]">
        <div className="flex items-center gap-2">
          <Radio size={14} className="as-gold animate-pulse"/>
          <div className="as-overline">Live Route · Powered by AeroSphere</div>
        </div>
        <a href={fr24Href} target="_blank" rel="noreferrer" className="as-mono text-[10px] uppercase tracking-widest text-[#A47E2A] hover:underline flex items-center gap-1" data-testid="fr24-link">
          Track on FlightRadar24 <ExternalLink size={11}/>
        </a>
      </div>
      <div ref={ref} className="w-full aspect-[16/9] bg-[#F1EDE4]" data-testid="route-map-canvas"/>
      {dep && arr && (
        <div className="p-4 border-t border-[#E5DFD2] grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Departure</div>
            <div className="as-serif text-xl mt-1">{dep.iata}</div>
            <div className="text-[11px] text-[#756E64]">{dep.city}, {dep.country}</div>
          </div>
          <div>
            <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Aircraft</div>
            <div className="as-serif text-xl mt-1 as-gold">{tail || "TBD"}</div>
            <div className="text-[11px] text-[#756E64]">Registration</div>
          </div>
          <div>
            <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Arrival</div>
            <div className="as-serif text-xl mt-1">{arr.iata}</div>
            <div className="text-[11px] text-[#756E64]">{arr.city}, {arr.country}</div>
          </div>
        </div>
      )}
    </div>
  );
}
